import { useState, useRef, useCallback } from 'react';
import { Upload, FileText, Image, Download, Trash2, Eye, X, Loader2 } from 'lucide-react';
import type { ExpenseDocument } from '@peles/shared';
import {
  useExpenseDocuments,
  useAddExpenseDocument,
  useRemoveExpenseDocument,
} from '../../hooks/useExpenseDocuments';
import { useDialog } from '../../context/DialogContext';
import { getPresignedUploadUrl, uploadFileToS3 } from '../../api/photos';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 25 * 1024 * 1024; // 25MB

interface ExpenseDocumentsSectionProps {
  expenseItemId: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function isPdfMime(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

export function ExpenseDocumentsSection({ expenseItemId }: ExpenseDocumentsSectionProps) {
  const { data: documents = [], isLoading } = useExpenseDocuments(expenseItemId);
  const addDocument = useAddExpenseDocument();
  const removeDocument = useRemoveExpenseDocument();
  const { confirm } = useDialog();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewDoc, setPreviewDoc] = useState<ExpenseDocument | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError('Only PDF and image files are supported.');
        return;
      }
      if (file.size > MAX_SIZE) {
        setUploadError('File exceeds the 25 MB limit.');
        return;
      }

      setUploadError('');
      setIsUploading(true);

      try {
        const presigned = await getPresignedUploadUrl({
          fileName: file.name,
          mimeType: file.type,
        });
        await uploadFileToS3(presigned.uploadUrl, file);
        await addDocument.mutateAsync({
          expenseItemId,
          doc: {
            s3Key: presigned.s3Key,
            fileName: file.name,
            mimeType: file.type,
            fileSizeBytes: file.size,
          },
        });
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Failed to upload document.');
      } finally {
        setIsUploading(false);
      }
    },
    [expenseItemId, addDocument],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files[0]);
      }
    },
    [handleUpload],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleUpload(e.target.files[0]);
        e.target.value = '';
      }
    },
    [handleUpload],
  );

  const handleRemoveDocument = useCallback(
    async (docId: string) => {
      if (
        await confirm({
          title: 'Remove Document',
          message: 'Remove this document?',
          confirmLabel: 'Remove',
        })
      ) {
        removeDocument.mutate({ expenseItemId, docId });
      }
    },
    [expenseItemId, removeDocument, confirm],
  );

  const handleDownload = useCallback((doc: ExpenseDocument) => {
    if (doc.downloadUrl) {
      window.open(doc.downloadUrl, '_blank');
    }
  }, []);

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Documents</h3>

      {/* Upload area */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors mb-3 ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
        data-testid="doc-upload-zone"
      >
        {isUploading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin text-primary-600" />
            <span className="text-sm text-gray-600">Uploading...</span>
          </div>
        ) : (
          <>
            <Upload size={20} className="mx-auto mb-1 text-gray-400" />
            <p className="text-xs text-gray-600">
              Drop PDF or image here, or{' '}
              <span className="text-primary-600 font-medium">browse</span>
            </p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf,image/jpeg,image/png,image/webp"
          onChange={handleInputChange}
          className="hidden"
          data-testid="doc-file-input"
        />
      </div>

      {uploadError && <p className="text-sm text-red-600 mb-2">{uploadError}</p>}

      {/* Document list */}
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading documents...</p>
      ) : documents.length === 0 ? (
        <p className="text-xs text-gray-400">No documents attached</p>
      ) : (
        <ul className="space-y-2" data-testid="doc-list">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {isImageMime(doc.mimeType) ? (
                  <Image size={16} className="text-blue-500 flex-shrink-0" />
                ) : (
                  <FileText size={16} className="text-red-500 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                  <p className="text-xs text-gray-400">
                    {formatFileSize(doc.fileSizeBytes)} &middot;{' '}
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {doc.downloadUrl && (isImageMime(doc.mimeType) || isPdfMime(doc.mimeType)) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewDoc(doc);
                    }}
                    className="text-gray-400 hover:text-primary-600 p-1"
                    aria-label="Preview document"
                    title="Preview"
                  >
                    <Eye size={14} />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(doc);
                  }}
                  className="text-gray-400 hover:text-primary-600 p-1"
                  aria-label="Download document"
                  title="Download"
                >
                  <Download size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveDocument(doc.id);
                  }}
                  className="text-gray-400 hover:text-red-600 p-1"
                  aria-label="Remove document"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal document={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}

export interface DocumentPreviewModalProps {
  document: ExpenseDocument;
  onClose: () => void;
}

export function DocumentPreviewModal({ document: doc, onClose }: DocumentPreviewModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div className="relative w-full h-full sm:h-[90vh] sm:max-w-4xl sm:mx-4 bg-white sm:rounded-lg shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1">{doc.fileName}</h3>
          <div className="flex items-center gap-2">
            {doc.downloadUrl && (
              <a
                href={doc.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-600 p-1"
                aria-label="Download"
              >
                <Download size={18} />
              </a>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
              aria-label="Close preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          {isPdfMime(doc.mimeType) && doc.downloadUrl ? (
            <iframe src={doc.downloadUrl} className="w-full h-full" title={doc.fileName} />
          ) : isImageMime(doc.mimeType) && doc.downloadUrl ? (
            <img
              src={doc.downloadUrl}
              alt={doc.fileName}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="text-center p-8">
              <FileText size={48} className="mx-auto mb-3 text-gray-400" />
              <p className="text-sm text-gray-600">Preview not available for this file type.</p>
              {doc.downloadUrl && (
                <a
                  href={doc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:underline mt-2 inline-block"
                >
                  Download to view
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
